import type { PaletteEntry } from '../engine/types';

/**
 * DMC six-strand embroidery floss (Art. 117) — a deliberately modest palette.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  READ THIS BEFORE YOU BUY THREAD
 *
 *  DMC does not publish official hex values. Every `hex` below is a community
 *  approximation sampled from photographs of real thread cards. Screens lie:
 *  monitor calibration, ambient light and dye lots all shift how a colour
 *  reads. Treat these swatches as "roughly this family", never as a match.
 *
 *  The `dmc` CODES are the shoppable part and are the thing to trust — but a
 *  human should still eyeball a real DMC colour card before spending money.
 *  Codes and names here were cross-checked against community DMC charts, not
 *  against DMC's own catalogue, so a transcription error is possible.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Keeping the palette small is a feature: 47 colours that a hobby shop actually
 * stocks beats 489 colours she has to mail-order.
 */

/** Broad hue families, used by the no-AI brief interpreter to match words. */
export type HueFamily =
  | 'red'
  | 'pink'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'brown'
  | 'neutral';

export interface FlossColor extends PaletteEntry {
  family: HueFamily;
  /** Rough lightness 0 (darkest) - 100 (lightest), for building gradients. */
  light: number;
}

export const DMC_SKEIN_METERS = 8;
export const DMC_SKEIN_YARDS = 8.7;
export const DMC_STRANDS_PER_SKEIN = 6;

export const FLOSS: readonly FlossColor[] = [
  // ── Reds & pinks ────────────────────────────────────────────────────────────
  { dmc: '498', name: 'Red — Dark', hex: '#A7132B', family: 'red', light: 28 },
  { dmc: '321', name: 'Red', hex: '#C8141D', family: 'red', light: 38 },
  { dmc: '666', name: 'Red — Bright', hex: '#DD1818', family: 'red', light: 46 },
  { dmc: '602', name: 'Cranberry — Medium', hex: '#CC1F6E', family: 'pink', light: 44 },
  { dmc: '3805', name: 'Cyclamen Pink', hex: '#EA3C8D', family: 'pink', light: 56 },
  { dmc: '956', name: 'Geranium', hex: '#F9627F', family: 'pink', light: 66 },
  { dmc: '962', name: 'Dusty Rose — Medium', hex: '#DF8FAC', family: 'pink', light: 74 },
  { dmc: '818', name: 'Baby Pink', hex: '#FFCDD7', family: 'pink', light: 90 },
  { dmc: '3713', name: 'Salmon — Very Light', hex: '#FFD5D0', family: 'pink', light: 92 },

  // ── Oranges & yellows ───────────────────────────────────────────────────────
  { dmc: '947', name: 'Burnt Orange', hex: '#FF5B00', family: 'orange', light: 55 },
  { dmc: '740', name: 'Tangerine', hex: '#FF7300', family: 'orange', light: 60 },
  { dmc: '741', name: 'Tangerine — Medium', hex: '#FF9300', family: 'orange', light: 68 },
  { dmc: '742', name: 'Tangerine — Light', hex: '#FFD058', family: 'orange', light: 82 },
  { dmc: '444', name: 'Lemon — Dark', hex: '#FFCC00', family: 'yellow', light: 80 },
  { dmc: '307', name: 'Lemon', hex: '#FDF305', family: 'yellow', light: 88 },
  { dmc: '727', name: 'Topaz — Very Light', hex: '#FFF3A2', family: 'yellow', light: 94 },

  // ── Greens ──────────────────────────────────────────────────────────────────
  { dmc: '699', name: 'Green', hex: '#28781B', family: 'green', light: 30 },
  { dmc: '700', name: 'Green — Bright', hex: '#007C2F', family: 'green', light: 34 },
  { dmc: '505', name: 'Jade Green', hex: '#3D8C5E', family: 'green', light: 44 },
  { dmc: '702', name: 'Kelly Green', hex: '#3AB54A', family: 'green', light: 58 },
  { dmc: '704', name: 'Chartreuse — Bright', hex: '#7CC328', family: 'green', light: 66 },
  { dmc: '907', name: 'Parrot Green — Light', hex: '#6AD718', family: 'green', light: 72 },

  // ── Blues ───────────────────────────────────────────────────────────────────
  { dmc: '820', name: 'Royal Blue — Very Dark', hex: '#142366', family: 'blue', light: 18 },
  { dmc: '798', name: 'Delft Blue — Dark', hex: '#3657A0', family: 'blue', light: 40 },
  { dmc: '995', name: 'Electric Blue — Dark', hex: '#0096BA', family: 'blue', light: 56 },
  { dmc: '3843', name: 'Electric Blue', hex: '#00B3C6', family: 'blue', light: 64 },
  { dmc: '3846', name: 'Bright Turquoise — Light', hex: '#18C3C7', family: 'blue', light: 70 },
  { dmc: '996', name: 'Electric Blue — Medium Light', hex: '#26C6DA', family: 'blue', light: 76 },

  // ── Purples ─────────────────────────────────────────────────────────────────
  { dmc: '550', name: 'Violet — Very Dark', hex: '#470768', family: 'purple', light: 20 },
  { dmc: '333', name: 'Blue Violet — Very Dark', hex: '#5C5478', family: 'purple', light: 36 },
  { dmc: '208', name: 'Lavender — Very Dark', hex: '#835B8B', family: 'purple', light: 46 },
  { dmc: '552', name: 'Violet — Medium', hex: '#9861AF', family: 'purple', light: 54 },
  { dmc: '553', name: 'Violet', hex: '#CFA1DB', family: 'purple', light: 78 },

  // ── Browns & tans ───────────────────────────────────────────────────────────
  { dmc: '433', name: 'Brown — Medium', hex: '#7A451F', family: 'brown', light: 32 },
  { dmc: '434', name: 'Brown — Light', hex: '#985E33', family: 'brown', light: 42 },
  { dmc: '436', name: 'Tan', hex: '#CB9051', family: 'brown', light: 62 },
  { dmc: '437', name: 'Tan — Light', hex: '#E4BB8E', family: 'brown', light: 78 },
  { dmc: '738', name: 'Tan — Very Light', hex: '#EFD5A6', family: 'brown', light: 86 },
  { dmc: '739', name: 'Tan — Ultra Very Light', hex: '#F8E2C7', family: 'brown', light: 92 },
  { dmc: '842', name: 'Beige Brown — Very Light', hex: '#D6BAA9', family: 'brown', light: 80 },

  // ── Neutrals ────────────────────────────────────────────────────────────────
  { dmc: '310', name: 'Black', hex: '#000000', family: 'neutral', light: 2 },
  { dmc: '413', name: 'Pewter Gray — Dark', hex: '#5B5B5B', family: 'neutral', light: 36 },
  { dmc: '415', name: 'Pearl Gray', hex: '#D5D5D5', family: 'neutral', light: 84 },
  { dmc: 'ECRU', name: 'Ecru', hex: '#F0E3C5', family: 'neutral', light: 90 },
  { dmc: '3865', name: 'Winter White', hex: '#F5F0EB', family: 'neutral', light: 95 },
  { dmc: 'BLANC', name: 'White', hex: '#F8F8F8', family: 'neutral', light: 97 },
  { dmc: 'B5200', name: 'Snow White', hex: '#FFFFFF', family: 'neutral', light: 100 },
] as const;

const BY_CODE = new Map(FLOSS.map((f) => [f.dmc.toUpperCase(), f]));

export function flossByCode(code: string): FlossColor | undefined {
  return BY_CODE.get(code.trim().toUpperCase());
}

export function flossIndexByCode(code: string): number {
  return FLOSS.findIndex((f) => f.dmc.toUpperCase() === code.trim().toUpperCase());
}

export function byFamily(family: HueFamily): FlossColor[] {
  return FLOSS.filter((f) => f.family === family);
}

/** Strip a palette entry down to the fields a serialised design needs. */
export function toPaletteEntry(f: FlossColor): PaletteEntry {
  return { dmc: f.dmc, name: f.name, hex: f.hex };
}

/**
 * Nearest floss colour to an arbitrary RGB triple. Used by photo import, which
 * must work with no AI at all: we sample the photo, then snap every sample to a
 * thread she can actually buy.
 *
 * Distance is a weighted-RGB approximation ("redmean"). It is not CIEDE2000,
 * but it is dependency-free and comfortably good enough to tell a dusty rose
 * from a burnt orange.
 */
/**
 * Closest floss in the palette to a given colour.
 *
 * Accepts either a hex string (what a colour picker gives you) or an RGB
 * triple (what canvas pixel sampling gives you), because both callers exist
 * and silently returning the first floss in the list for the wrong input type
 * is exactly the kind of bug that ships a bracelet in the wrong colour.
 */
export function nearestFloss(color: string | [number, number, number]): FlossColor {
  const rgb = typeof color === 'string' ? hexToRgb(color) : color;
  if (rgb.length !== 3 || rgb.some((c) => !Number.isFinite(c))) {
    throw new TypeError(`nearestFloss: not a colour: ${JSON.stringify(color)}`);
  }
  let best = FLOSS[0]!;
  let bestD = Number.POSITIVE_INFINITY;
  for (const f of FLOSS) {
    const d = redmean(rgb, hexToRgb(f.hex));
    if (d < bestD) {
      bestD = d;
      best = f;
    }
  }
  return best;
}

export function redmean(a: [number, number, number], b: [number, number, number]): number {
  const rMean = (a[0] + b[0]) / 2;
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return Math.sqrt(
    (2 + rMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMean) / 256) * db * db,
  );
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const n = Number.parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Perceived luminance 0-255, used to pick readable label text over a swatch. */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
